import * as PIXI from 'pixi.js-legacy';
import project from '../util/project';
import Item from '../Item';
import InnerDropArea from '../InnerDropArea';
import OuterDropArea from '../OuterDropArea';
import geometry from '../util/geometry';
import constants from '../../settings/constants';
import productsService, { getProduct, isType } from '../../services/products';
import { mergePolygons } from '../../util/mergePolygons';
import { ITEMS } from '../../constants';
import productService from '../../services/products';

export default class Section extends Item {
  constructor(...args) {
    super(...args);

    this.drawChildren();
  }

  draw() {
    const { item, ratio } = this;
    const outerSize = project.outer(item);
    const faceSize = project.face(item);
    const postWidth = productService.getPostWidth();

    const postVertices = project.calculateVertices({
      height: item.height,
      width: postWidth,
      depth: postWidth,
    });

    const frontBottomLeft = project.point(postVertices[0]);
    const backBottomLeft = project.point(postVertices[4]);

    const offsetX = (backBottomLeft[0] - frontBottomLeft[0]) * ratio;
    const offsetY = (backBottomLeft[1] - frontBottomLeft[1]) * ratio;

    const vertices = project.calculateVertices(item);
    const projectedVertices = vertices.map(project.point);
    const frontBottom = projectedVertices[0];

    const scaledVertices = projectedVertices.map(vertex => {
      return [
        (vertex[0] - frontBottom[0]) * ratio,
        (outerSize.height - (vertex[1] - frontBottom[1])) * ratio,
      ];
    });

    const itemHeight = this.item.height / 10;

    const { color } = this.item.filter;

    const textures = {
      brace:
        PIXI.Loader.shared.resources[
          `brace_${this.item.width === 850 ? '84' : '64'}_${color}`
        ].texture,
      bl: PIXI.Loader.shared.resources[`${itemHeight}_back_left_${color}`]
        .texture,
      br: PIXI.Loader.shared.resources[`${itemHeight}_back_right_${color}`]
        .texture,
      bro: PIXI.Loader.shared.resources[
        `${itemHeight}_back_right_over_${color}`
      ].texture,
      fl: PIXI.Loader.shared.resources[`${itemHeight}_front_left_${color}`]
        .texture,
      fr: PIXI.Loader.shared.resources[`${itemHeight}_front_right_${color}`]
        .texture,
    };

    this.leftDropAreasContainer =
      this.leftDropAreasContainer || new PIXI.Container();
    this.leftDropAreasContainer.x = 0;

    if (!this.leftDropAreasContainer.parent) {
      this.addChild(this.leftDropAreasContainer);
    }

    this.brace = this.brace || new PIXI.Sprite();
    this.brace.texture = textures.brace;
    this.brace.width =
      constants.CROSS_BRACE_DIMENSIONS[item.width].width * ratio;
    this.brace.height =
      constants.CROSS_BRACE_DIMENSIONS[item.width].height * ratio;
    this.brace.x = scaledVertices[7][0] + offsetX;
    this.brace.y = (constants.DISTANCE_BETWEEN_ATTACHMENTS + 30) * ratio;

    if (!this.brace.parent) {
      this.addChild(this.brace);
    }

    this.innerDropAreasContainer =
      this.innerDropAreasContainer || new PIXI.Container();
    this.innerDropAreasContainer.x =
      scaledVertices[4][0] - scaledVertices[0][0];

    if (!this.innerDropAreasContainer.parent) {
      this.addChild(this.innerDropAreasContainer);
    }

    this.back = this.back || new PIXI.Container();
    this.back.x = scaledVertices[7][0];

    if (!this.back.parent) {
      this.addChild(this.back);
    }

    this.bl = this.bl || new PIXI.Sprite();
    this.bl.texture = textures.bl;
    this.bl.height = faceSize.height * ratio + offsetY;
    this.bl.width =
      faceSize.height * (textures.bl.width / textures.bl.height) * ratio;
    this.bl.x = -offsetX;
    this.bl.y = 0;

    if (!this.bl.parent) {
      this.back.addChild(this.bl);
    }

    this.br = this.br || new PIXI.Sprite();
    this.br.texture = textures.br;
    this.br.height = faceSize.height * ratio + offsetY;
    this.br.width = this.bl.height * (textures.br.width / textures.br.height);
    this.br.x = item.width * ratio - this.br.width + 1;
    this.br.y = 0;

    if (!this.br.parent) {
      this.back.addChild(this.br);
    }

    this.childrenContainer = this.childrenContainer || new PIXI.Container();
    this.childrenContainer.x = scaledVertices[4][0] - scaledVertices[0][0];

    if (!this.childrenContainer.parent) {
      this.addChild(this.childrenContainer);
    }

    this.front = this.front || new PIXI.Container();
    this.front.y = scaledVertices[3][1];

    if (!this.front.parent) {
      this.addChild(this.front);
    }

    this.bro = this.bro || new PIXI.Sprite();
    this.bro.height = faceSize.height * ratio;
    this.bro.width =
      this.bro.height * (textures.bro.width / textures.bro.height);
    this.bro.x = item.width * ratio - this.bro.width;
    this.bro.y = 0;

    if (!this.bro.parent) {
      this.front.addChild(this.bro);
    }

    this.fl = this.fl || new PIXI.Sprite();
    this.fl.texture = textures.fl;
    this.fl.height = faceSize.height * ratio;
    this.fl.width = this.fl.height * (textures.fl.width / textures.fl.height);
    this.fl.x = -1;
    this.fl.y = 0;

    if (!this.fl.parent) {
      this.front.addChild(this.fl);
    }

    this.fr = this.fr || new PIXI.Sprite();
    this.fr.texture = textures.fr;
    this.fr.height = faceSize.height * ratio;
    this.fr.width = this.fr.height * (textures.fr.width / textures.fr.height);
    this.fr.x = item.width * ratio - this.fr.width + offsetX;
    this.fr.y = 0;

    if (!this.fr.parent) {
      this.front.addChild(this.fr);
    }

    this.rightDropAreasContainer =
      this.rightDropAreasContainer || new PIXI.Container();
    this.rightDropAreasContainer.x = this.item.width * this.ratio;

    if (!this.rightDropAreasContainer.parent) {
      this.addChild(this.rightDropAreasContainer);
    }

    this.data = {
      vertices,
      projectedVertices,
      scaledVertices,
      outerSize,
      faceSize,
    };

    this.drawChildren();
  }

  setHitArea() {
    const { scaledVertices } = this.data;

    const vertices = [
      scaledVertices[0],
      scaledVertices[1],
      scaledVertices[5],
      scaledVertices[6],
      scaledVertices[7],
      scaledVertices[3],
    ];

    let polygon = {
      regions: [vertices],
    };

    this.childrenContainer.children.forEach(child => {
      if (geometry.extendsOutside(child.item, this.item)) {
        const scaledVertices = child.data.scaledVertices;

        const vertices = [
          scaledVertices[0],
          scaledVertices[1],
          scaledVertices[5],
          scaledVertices[6],
          scaledVertices[7],
          scaledVertices[3],
        ].map(vertex => [
          vertex[0] + child.x + child.parent.x,
          vertex[1] + child.y + child.parent.y,
        ]);

        polygon = mergePolygons(polygon, {
          regions: [vertices],
        });
      }
    });

    this.hitArea = new PIXI.Polygon([].concat(...polygon.regions[0]));
  }

  sortChildren() {
    // Always draw BROR inserts from lowest to highest.
    this.childrenContainer.children.sort((a, b) => {
      if (
        [a, b].some(sprite => productsService.isType(sprite.item, 'add-on'))
      ) {
        return a.item.x - b.item.x;
      }
      return a.item.y - b.item.y;
    });
  }

  addItem(item) {
    const sprite = new Item({
      options: this.options,
      item: item,
      parentItem: this.item,
      ratio: this.ratio,
      onDragStart: this.onDragStart,
      app: this.app,
    });

    this.childrenContainer.addChild(sprite);

    this.sprites.set(item.itemid, sprite);

    this.sortChildren();

    return sprite;
  }

  addSprite(sprite, slot) {
    const item = Object.assign({}, sprite.item);

    const product = productsService.getFit(item, slot);

    // only shelves (including add on) will return another object
    Object.assign(item, product);

    item.x = slot.x - this.item.x;
    item.y = slot.y - this.item.y;
    item.z = slot.z - this.item.z;

    sprite.update({
      item: item,
      parentItem: this.item,
      options: this.options,
    });

    this.childrenContainer.addChild(sprite);
    this.displayDropAreas(slot);

    sprite.once('added', () => {
      if (sprite.parent !== this.childrenContainer) {
        this.displayDropAreas();
      }
    });

    this.sortChildren();

    return sprite;
  }

  itemBelongingToSlot(slot) {
    return getProduct({ id: slot.id });
  }

  drawDropAreas(slots) {
    slots = slots.filter(slot => slot.parent.itemid === this.item.itemid);

    const width = Math.floor(this.item.width * this.ratio) + 1;

    slots.forEach(slot => {
      const x = slot.x - this.item.x;

      if (x >= 0 && x < this.item.width) {
        const sprite = new InnerDropArea({
          width: width,
          height: 100 * this.ratio,
          ratio: this.ratio,
          slot: slot,
        });

        sprite.slot = slot;

        sprite.x = 0;
        sprite.y =
          (this.item.height -
            (slot.y + 50 + (constants.DROP_AREA_MAX_HEIGHT - 50) / 2)) *
          this.ratio;
        if (isType(this.itemBelongingToSlot(slot), ITEMS.DRAWER)) {
          sprite.y -= (215 / 2) * this.ratio;
        }
        this.innerDropAreasContainer.addChild(sprite);
      } else {
        const sprite = new OuterDropArea({
          width: width,
          height: 100 * this.ratio,
          ratio: this.ratio,
          slot: slot,
        });

        sprite.slot = slot;
        sprite.y = (this.item.height - (slot.y + 126)) * this.ratio;

        if (x >= this.item.width) {
          this.rightDropAreasContainer.addChild(sprite);
        } else {
          sprite.x = -slot.width * this.ratio - 2;

          this.leftDropAreasContainer.addChild(sprite);
        }
      }
    });
  }

  eraseDropAreas() {
    this.leftDropAreasContainer.removeChildren();
    this.rightDropAreasContainer.removeChildren();
    this.innerDropAreasContainer.removeChildren();
  }

  displayDropAreas(slot) {
    const dropAreas = this.innerDropAreasContainer.children.concat(
      this.leftDropAreasContainer.children,
      this.rightDropAreasContainer.children
    );

    const slotPadding =
      slot && productsService.isType(slot, ITEMS.DRAWER)
        ? {
            // Drawers will collide with drop area above but shouldn't hide it
            top: -20,
          }
        : {};

    dropAreas.forEach(dropArea => {
      if (
        slot &&
        slot.y <= dropArea.slot.y &&
        geometry.collides(slot, dropArea.slot, slotPadding)
      ) {
        dropArea.visible = false;
      } else {
        dropArea.visible = true;
      }
    });
  }
}
